import React, { useState, useEffect, useRef, useCallback } from "react";
import { Link } from "react-router-dom";
import Confetti from "react-confetti";
import { generateProblem } from "../elo";
import { buttonStyle, circleButtonStyle } from "../utils/styleConstants";
import useCanvas from "../hooks/useCanvas";
import ProblemCard from "./ProblemCard";
import LinksAvatar from "./LinksAvatar";
import AnswerBox from "./AnswerBox";
import TrialModal from "./TrialModal";
import Tutorial from "./Tutorial"; // Import the new Tutorial component
import useHintSystem from "../hooks/useHintSystem";
import useAudio from "../hooks/useAudio";
import HintBox from "./HintBox";
import ConfirmationModal from "./ConfirmationModal"; // Import the new component

const LandingPage = () => {
  // Change the default value to false so tutorial doesn't auto-start
  const [showTutorial, setShowTutorial] = useState(false);

  // Trial mode state (keep track invisibly)
  const [trialProblemsCount, setTrialProblemsCount] = useState(0);
  const [showTrialModal, setShowTrialModal] = useState(false);
  const [isFadingOut, setIsFadingOut] = useState(false);

  // State variables for math problems and answers
  const [problem, setProblem] = useState({ display: "10 + 4", eval: "10 + 4" });
  const [userAnswer, setUserAnswer] = useState("");
  const [wrongAttempts, setWrongAttempts] = useState(0);
  const [isWrongAnswer, setIsWrongAnswer] = useState(false);
  const [isCorrectAnswer, setIsCorrectAnswer] = useState(false);
  const [streakCount, setStreakCount] = useState(0);
  const [problemStartTime, setProblemStartTime] = useState(Date.now());

  // A simplified initial problem has been generated
  const initialProblemGenerated = useRef(true);

  // Import custom hooks - just what we need for the trial
  const {
    canvasRef,
    currentTool,
    setCurrentTool,
    handlePointerDown,
    draw,
    stopDrawing,
    clearCanvas,
    undoLastStroke, // Add the new undo function
    hasDrawings, // Add the hasDrawings state
  } = useCanvas();

  const {
    audioPermission,
    hintActive,
    setHintActive,
    isClarificationPlaying,
    setIsClarificationPlaying,
    playAudio,
    stopAudio,
    hintAudioRef,
    audioError,
  } = useAudio();

  // Create a local state for isHintLoading instead of trying to use it from the hook
  const [isHintLoading, setIsHintLoading] = useState(false);

  // Initialize useHintSystem with problem, playAudio, and state setters
  const {
    hint,
    setHint,
    hintCount,
    setHintCount,
    timings,
    hintBoxRef,
    hintClosing,
    showClarificationButton,
    requestHint: baseRequestHint,
    requestClarification: baseRequestClarification,
    handleCloseHint: baseHandleCloseHint,
  } = useHintSystem(
    problem,
    playAudio,
    setHintActive,
    setIsClarificationPlaying
  );

  // Confetti state
  const [confettiInstances, setConfettiInstances] = useState([]);

  // Function to add a new confetti celebration
  const triggerConfetti = () => {
    const id = Date.now(); // Use timestamp as unique ID

    // Add new confetti instance to the array
    setConfettiInstances((prev) => [
      ...prev,
      {
        id,
        position: {
          x: window.innerWidth / 2,
          y: 30,
          w: 0,
          h: 0,
        },
      },
    ]);

    // Remove this confetti instance after animation completes
    setTimeout(() => {
      setConfettiInstances((prev) =>
        prev.filter((instance) => instance.id !== id)
      );
    }, 3000);
  };

  // Simplified generate new problem function
  const generateNewProblem = useCallback(() => {
    try {
      // Get the current problem from state directly instead of from closure
      const currentProblem = problem.display;
      let newProblem;
      let attempts = 0;
      const maxAttempts = 5; // Prevent infinite loops

      do {
        newProblem = generateProblem(1, 0); // Always from unit 1, beginner level
        attempts++;
      } while (
        // Continue generating if it's the same as the current problem (up to maxAttempts)
        (typeof newProblem === "string" ? newProblem : newProblem.display) ===
          currentProblem &&
        attempts < maxAttempts
      );

      setProblem(
        typeof newProblem === "string"
          ? { display: newProblem, eval: newProblem }
          : newProblem
      );
      setProblemStartTime(Date.now());
    } catch (error) {
      console.error("Error generating problem:", error);
      // Fallback problem
      setProblem({ display: "5 + 3", eval: "5 + 3" });
    }
  }, []); // Remove problem from dependency array to prevent infinite loop

  // Add audio ref for success ding
  const successAudioRef = useRef(new Audio("/audio/ding.mp3"));
  // Set a lower volume (0.3 is 30% of maximum volume)
  successAudioRef.current.volume = 0.3;

  // Handle answer submission - simplified for trial version
  const handleAnswerSubmit = async () => {
    try {
      const correctAnswer = eval(problem.eval || problem);
      const isCorrect = parseFloat(userAnswer) === correctAnswer;

      if (isCorrect) {
        // Play success sound when answer is correct
        successAudioRef.current.currentTime = 0; // Reset audio to start
        successAudioRef.current.play();

        // Visual feedback first - trigger confetti
        triggerConfetti();
        setIsWrongAnswer(false);
        setIsCorrectAnswer(true);

        // Update streak count
        setStreakCount((prevStreak) => prevStreak + 1);

        // Reset states
        setUserAnswer("");
        setWrongAttempts(0);

        // Clear canvas directly without confirmation
        clearCanvasWithoutConfirmation();

        // Increment count invisibly
        const newCount = trialProblemsCount + 1;
        setTrialProblemsCount(newCount);

        // Check if trial limit reached
        if (newCount >= 5) {
          setIsFadingOut(true);

          // After fade out animation, show login modal
          setTimeout(() => {
            setShowTrialModal(true);
          }, 1000);

          return;
        }

        // Generate a new problem
        generateNewProblem();

        // Reset the correct answer state after a delay
        setTimeout(() => {
          setIsCorrectAnswer(false);
        }, 2000);
      } else {
        // Reset streak on wrong answers
        setStreakCount(0);
        setIsCorrectAnswer(false);

        // Handle incorrect answer
        setWrongAttempts(wrongAttempts + 1);
        setIsWrongAnswer(true);
        setUserAnswer("");
        setTimeout(() => setIsWrongAnswer(false), 500);
      }
    } catch (error) {
      console.error("Error checking answer:", error);
      setUserAnswer("");
    }
  };

  // Initialize component with first problem - using useEffect with empty dependency array
  useEffect(() => {
    // Generate problem only once when component mounts
    generateNewProblem();
  }, []); // Empty dependency array ensures it only runs once

  // Add a "Take Tour" button to relaunch tutorial
  const handleShowTutorial = () => {
    setShowTutorial(true);
  };

  // Close the tutorial
  const handleCloseTutorial = () => {
    setShowTutorial(false);
  };

  // Add state for hint button animation and hint functionality
  const [isHintButtonShaking, setIsHintButtonShaking] = useState(false);
  const inactivityTimerRef = useRef(null);

  // Function to reset inactivity timer
  const resetInactivityTimer = useCallback(() => {
    // Clear any existing timer
    if (inactivityTimerRef.current) {
      clearTimeout(inactivityTimerRef.current);
    }

    // Stop shaking if it was shaking
    setIsHintButtonShaking(false);

    // Set new timer for 10 seconds
    inactivityTimerRef.current = setTimeout(() => {
      // Start shaking after 10 seconds of inactivity
      setIsHintButtonShaking(true);
    }, 10000);
  }, []);
  // Reset the timer when component mounts
  useEffect(() => {
    resetInactivityTimer();

    return () => {
      // Clean up the timer when component unmounts
      if (inactivityTimerRef.current) {
        clearTimeout(inactivityTimerRef.current);
      }
    };
  }, [resetInactivityTimer]);

  // Reset inactivity timer when a new problem is generated
  useEffect(() => {
    resetInactivityTimer();
  }, [problem, resetInactivityTimer]);

  // Create a local state for the trial hint
  const [trialHint, setTrialHint] = useState("");
  const [isHintClosing, setIsHintClosing] = useState(false);

  // Handle request hint for trial version - UPDATED to use the actual hint system
  const requestHint = async () => {
    try {
      resetInactivityTimer();
      // Set loading state to true before requesting hint
      setIsHintLoading(true);
      // Use the actual hint system from useHintSystem hook
      await baseRequestHint(canvasRef.current);
      // Set loading state to false after hint is loaded
      setIsHintLoading(false);
    } catch (error) {
      console.error("Error requesting hint:", error);
      // Make sure to set loading to false in case of error
      setIsHintLoading(false);
    }
  };

  // Add proper hint clarification functionality
  const requestClarification = async () => {
    try {
      await baseRequestClarification(canvasRef.current);
    } catch (error) {
      console.error("Error requesting clarification:", error);
    }
  };

  // Use the proper hint closing functionality
  const handleCloseHint = () => {
    baseHandleCloseHint(stopAudio);
  };

  // Modify the draw function to reset the inactivity timer
  const handleDraw = (e) => {
    draw(e);
    // Reset the inactivity timer whenever the user draws
    resetInactivityTimer();
  };

  // Add state for confirmation modal
  const [showClearConfirmation, setShowClearConfirmation] = useState(false);

  // Function to show confirmation modal
  const handleClearCanvas = () => {
    setShowClearConfirmation(true);
  };

  // Function to clear canvas directly without confirmation
  const clearCanvasWithoutConfirmation = () => {
    clearCanvas();
  };

  // Function to actually clear the canvas after confirmation
  const confirmClearCanvas = () => {
    clearCanvas();
    setShowClearConfirmation(false);
  };

  // Function to cancel clearing
  const cancelClearCanvas = () => {
    setShowClearConfirmation(false);
  };

  const [isNumpadVisible, setIsNumpadVisible] = useState(false);

  // Add contact form state
  const [showContactForm, setShowContactForm] = useState(false);
  const [contactName, setContactName] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactMessage, setContactMessage] = useState("");
  const [contactSubmitted, setContactSubmitted] = useState(false);
  
  // Handle contact form submission
  const handleContactSubmit = (e) => {
    e.preventDefault();
    
    // Construct mailto URL with form data
    const subject = encodeURIComponent(`Links Support Request from ${contactName}`);
    const body = encodeURIComponent(`Name: ${contactName}\nEmail: ${contactEmail}\n\nMessage: ${contactMessage}`);
    const mailtoUrl = `mailto:brockman.michael2003@gmail.com?subject=${subject}&body=${body}`;
    
    // Open default email client
    window.open(mailtoUrl);
    
    // Reset form and show confirmation
    setContactSubmitted(true);
    
    // Reset form after a delay
    setTimeout(() => {
      setContactName("");
      setContactEmail("");
      setContactMessage("");
      setContactSubmitted(false);
      setShowContactForm(false);
    }, 3000);
  };

  return (
    <div
      className="App"
      style={{
        height: "100vh",
        width: "100vw",
        overflow: "hidden",
        position: "relative",
        backgroundColor: "#fff9e9",
        fontFamily: "'Inter', sans-serif",
        opacity: isFadingOut ? 0.3 : 1,
        transition: "opacity 1s ease",
      }}
    >
      {/* CSS animations and responsive styles */}
      <style>{`
        @keyframes rotationalShake {
          0% { transform: rotate(0deg); }
          10% { transform: rotate(-10deg); }
          20% { transform: rotate(8deg); }
          30% { transform: rotate(-6deg); }
          40% { transform: rotate(4deg); }
          50% { transform: rotate(-2deg); }
          60% { transform: rotate(1deg); }
          70% { transform: rotate(-0.5deg); }
          80% { transform: rotate(0.25deg); }
          90% { transform: rotate(-0.1deg); }
          100% { transform: rotate(0deg); }
        }
        
        /* Responsive styles for iPhone/mobile */
        @media (max-width: 767px) {
          .login-signup-container {
            position: fixed !important;
            top: 10px !important;
            left: 0 !important;
            right: 0 !important;
            justify-content: center !important;
          }
          
          .login-signup-button {
            padding: 6px 12px !important;
            font-size: 14px !important;
          }
          
          .problem-card {
            top: 60px !important; /* Push down below the login buttons */
          }
        }
        
        .footer-links {
          display: flex;
          justify-content: center;
          align-items: center;
          gap: 20px;
        }
      `}</style>

      {/* Add centered footer with Terms, Privacy, and Contact Links */}
      <div
        style={{
          position: "fixed",
          bottom: "10px",
          left: "0",
          right: "0",
          zIndex: 10,
          display: "flex",
          justifyContent: "center",
          fontSize: "12px",
        }}
      >
        <div className="footer-links">
          <Link
            to="/terms"
            style={{
              color: "#555",
              textDecoration: "none",
            }}
          >
            Terms of Service
          </Link>
          <Link
            to="/privacy"
            style={{
              color: "#555",
              textDecoration: "none",
            }}
          >
            Privacy Policy
          </Link>
          <a
            href="mailto:brockman.michael2003@gmail.com?subject=Links%20Support%20Request"
            style={{
              color: "#555",
              textDecoration: "none",
            }}
          >
            Contact Us
          </a>
        </div>
      </div>
      
      {/* Confetti for correct answers */}
      {confettiInstances.map((instance) => (
        <Confetti
          key={instance.id}
          numberOfPieces={200}
          recycle={false}
          gravity={0.3}
          confettiSource={instance.position}
        />
      ))}

      {/* Canvas */}
      <canvas
        ref={canvasRef}
        onPointerDown={(e) => {
          handlePointerDown(e);
          resetInactivityTimer(); // Reset timer on pointer down
        }}
        onPointerMove={handleDraw} // Use the wrapped function
        onPointerUp={stopDrawing}
        onPointerLeave={stopDrawing}
        onTouchStart={(e) => {
          handlePointerDown(e);
          resetInactivityTimer(); // Reset timer on touch start
        }}
        onTouchMove={handleDraw} // Use the wrapped function
        onTouchEnd={stopDrawing}
        style={{
          position: "absolute",
          top: 0,
          left: 0,
          width: "100%",
          height: "100%",
          zIndex: 0,
        }}
      />

      {/* Problem Display - added class for selector */}
      <div
        className="problem-card"
        style={{
          position: "fixed",
          top: "0",
          left: "50%",
          transform: "translateX(-50%)",
          zIndex: 2,
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          width: "95%",
          maxWidth: "400px",
          paddingTop: "20px", // Add some padding from the very top
        }}
      >
        <ProblemCard
          mode="learn"
          isEditing={false}
          problem={problem}
          num1={0}
          num2={0}
          operator="+"
          showOperators={false}
          setNum1={() => {}}
          setNum2={() => {}}
          setOperator={() => {}}
          setShowOperators={() => {}}
          handleSave={() => {}}
          buttonStyle={buttonStyle}
          generateNewProblem={() => {}}
          setIsEditing={() => {}}
        />

        {/* Add a tutorial button below the problem card */}
        <button
          onClick={() => setShowTutorial(true)}
          style={{
            marginTop: "15px",
            backgroundColor: "#9cdaed",
            border: "none",
            borderRadius: "8px",
            padding: "8px 16px",
            fontSize: "16px",
            fontWeight: "600",
            cursor: "pointer",
            boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
            transition: "transform 0.2s ease, box-shadow 0.2s ease",
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)";
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.15)";
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)";
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)";
          }}
        >
          <div style={{ display: "flex", alignItems: "center", gap: "5px" }}>
            <span>Tutorial</span>
          </div>
        </button>
      </div>

      {/* Login/Signup buttons - now centered at the top on mobile */}
      <div
        className="login-signup-container"
        style={{
          position: "fixed",
          top: "20px",
          left: "20px",
          display: "flex",
          zIndex: 10,
          gap: "10px",
        }}
      >
        <Link
          to="/register"
          className="login-signup-button"
          style={{
            ...buttonStyle,
            backgroundColor: "#9CDAED",
            margin: 0,
            textDecoration: "none", // Remove underline
            transition: "transform 0.2s ease, box-shadow 0.2s ease", // Add smooth transition
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)"; // Enlarge button on hover
            e.currentTarget.style.boxShadow = "0 4px 8px rgba(0,0,0,0.2)"; // Add stronger shadow
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)"; // Reset size
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0,0,0,0.1)"; // Reset shadow
          }}
        >
          Sign Up
        </Link>
        <Link
          to="/login"
          className="login-signup-button"
          style={{
            ...buttonStyle,
            backgroundColor: "transparent",
            border: "2px solid #9CDAED",
            margin: 0,
            textDecoration: "none", // Remove underline
            transition: "transform 0.2s ease", // Add smooth transition
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.transform = "scale(1.05)"; // Enlarge button on hover
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.transform = "scale(1)"; // Reset size
          }}
        >
          Log In
        </Link>
      </div>

      {/* Remove the help button in the top right since we now have the tutorial button below the problem */}

      {/* Drawing Tools - Fix positioning to match MathApp.js */}
      <div
        style={{
          position: "fixed",
          top: window.innerWidth < 800 ? "auto" : "20px",
          bottom: window.innerWidth < 800 ? "100px" : "auto", 
          right: "20px",
          zIndex: 2,
          display: "flex",
          flexDirection: "column", // Stack buttons vertically
          gap: "10px",
        }}
      >
        {/* Standard drawing tools in a row */}
        <div
          style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
        >
          <button
            style={{
              ...circleButtonStyle,
              background:
                currentTool === "draw" ? "rgba(156, 218, 237, 0.9)" : "#f3e1b2",
            }}
            onClick={() => setCurrentTool("draw")}
          >
            ✏️
          </button>
          <button
            style={{
              ...circleButtonStyle,
              background:
                currentTool === "erase"
                  ? "rgba(156, 218, 237, 0.9)"
                  : "#f3e1b2",
            }}
            onClick={() => setCurrentTool("erase")}
          >
            <i
              className="fas fa-eraser"
              style={{
                fontSize: window.innerWidth < 800 ? "30px" : "45px",
                color: currentTool === "erase" ? "#ffb6c1" : "#ffb6c1",
              }}
            ></i>
          </button>
        </div>

        {/* Undo and clear buttons - only show if there are drawings */}
        {hasDrawings && (
          <div
            style={{ display: "flex", gap: "10px", justifyContent: "flex-end" }}
          >
            <button
              title="Undo Last Stroke"
              onClick={undoLastStroke}
              style={{
                ...circleButtonStyle,
                width: "50px",
                height: "50px",
                fontSize: "24px",
                background: "#f3e1b2",
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
              }}
            >
              <img
                src="/undo.png"
                alt="Undo"
                style={{
                  width: "25px",
                  height: "25px",
                  objectFit: "contain",
                }}
              />
            </button>
            <button
              title="Clear All"
              onClick={handleClearCanvas}
              style={{
                ...circleButtonStyle,
                width: "50px",
                height: "50px",
                fontSize: "24px",
                background: "#ff9999",
              }}
            >
              🗑️
            </button>
          </div>
        )}
      </div>

      {/* Answer Box */}
      <AnswerBox
        userAnswer={userAnswer}
        setUserAnswer={setUserAnswer}
        handleAnswerSubmit={handleAnswerSubmit}
        wrongAttempts={wrongAttempts}
        isWrongAnswer={isWrongAnswer}
        onNumpadVisibilityChange={setIsNumpadVisible}
      />

      {/* Links Avatar - Fix to match MathApp.js implementation */}
      <div
        style={{
          position: "fixed",
          left: "20px",
          bottom: "20px",
          zIndex: 2,
          animation:
            isHintButtonShaking &&
            !hintActive &&
            !isClarificationPlaying &&
            !isHintLoading
              ? "rotationalShake 1.5s ease-in-out infinite"
              : "none",
          transformOrigin: "center", // Set rotation origin to bottom center
        }}
      >
        <LinksAvatar
          onClick={() => {
            resetInactivityTimer();
            requestHint();
          }}
          isProcessing={isHintLoading}
          wrongAttempts={wrongAttempts}
          streakCount={streakCount}
          isCorrectAnswer={isCorrectAnswer}
          disabled={hintActive || isClarificationPlaying || isHintLoading}
        />
      </div>

      {/* Use the actual HintBox with full functionality */}
      <HintBox
        hint={hint}
        hintBoxRef={hintBoxRef}
        hintClosing={hintClosing}
        handleCloseHint={handleCloseHint}
        showClarificationButton={showClarificationButton}
        requestClarification={requestClarification}
        isClarificationPlaying={isClarificationPlaying}
        isHintLoading={isHintLoading}
        hintBoxBottom="10px" // Changed from 100px to 10px to match MathApp.js
        audioError={audioError}
      />

      {/* Trial modal when completed */}
      {showTrialModal && <TrialModal />}

      {/* Add the tutorial when showTutorial is true */}
      {showTutorial && <Tutorial onClose={handleCloseTutorial} />}

      {/* Clear Confirmation Modal */}
      {showClearConfirmation && (
        <ConfirmationModal
          message="Are you sure you want to clear your work?"
          onConfirm={confirmClearCanvas}
          onCancel={cancelClearCanvas}
        />
      )}
    </div>
  );
};

export default LandingPage;
